/**
 * rb-cart-mobile.js — mobile cart drawer (final spec, 26 Aug).
 *
 * 1. Swipe a row left to reveal Remove; tap the panel to remove.
 * 2. Remove stays a visible labelled button for anyone who doesn't swipe.
 * 3. Undo toast (5s) restoring variant + quantity + line properties.
 * 4. Quantity minus clamped at 1 so removal is never a hidden step-to-zero.
 * 5. Moves the "Complete the look" rail out of the scroller so it pins above
 *    the checkout footer.
 *
 * HARD RULE: this file must not set flex/height/overflow on, or otherwise
 * restructure, .ajaxcart-draw__wrapper / .ajaxcart__form / #CartContainer.
 * The container is already flex-column with a flex:1 scroller and flex:none
 * footer, so moving the rail between them is all that's needed. Re-declaring
 * those containers is what broke the drawer previously.
 *
 * Removal itself still runs through the theme's own `.ajaxcart__remove`
 * handler in ajax-cart.js — we only read the line first so Undo can rebuild it.
 */
(function () {
  'use strict';

  var MOBILE = 768;
  var UNDO_MS = 5000;
  var SWIPE_OPEN = 45;   // px left-drag to reveal
  var SWIPE_SHUT = 25;   // px right-drag to close
  var SWIPE_VTOL = 30;   // vertical slop before we treat it as a scroll

  var lastRemoved = null;
  var undoEl = null;
  var undoTimer = null;
  var cartCache = null;

  function isMobile() { return window.innerWidth < MOBILE; }
  function container() { return document.getElementById('CartContainer'); }

  /* ------------------------------------------------------------ cart data */

  function refreshCartCache() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cartCache = cart; return cart; })
      .catch(function () { return null; });
  }

  function lineDataFor(lineIndex) {
    if (!cartCache || !cartCache.items) return null;
    var item = cartCache.items[lineIndex - 1]; // ajaxCart lines are 1-based
    if (!item) return null;

    var props = {};
    if (item.properties) {
      Object.keys(item.properties).forEach(function (k) {
        if (item.properties[k] !== null && item.properties[k] !== '') props[k] = item.properties[k];
      });
    }
    var variantLabel = item.variant_title && item.variant_title !== 'Default Title'
      ? ' · ' + item.variant_title
      : '';
    return {
      id: item.variant_id,
      quantity: item.quantity,
      properties: props,
      selling_plan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.id : null,
      label: item.product_title + variantLabel
    };
  }

  /* -------------------------------------------------------------- undo UI */

  function ensureUndoEl() {
    if (undoEl && document.body.contains(undoEl)) return undoEl;
    undoEl = document.createElement('div');
    undoEl.className = 'rb-cart-undo';
    undoEl.setAttribute('role', 'status');
    undoEl.innerHTML =
      '<span class="rb-cart-undo__text"></span>' +
      '<button type="button" class="rb-cart-undo__btn">Undo</button>';
    undoEl.querySelector('.rb-cart-undo__btn').addEventListener('click', doUndo);
    document.body.appendChild(undoEl);
    return undoEl;
  }

  function showUndo(data) {
    if (!data) return;
    lastRemoved = data;
    var el = ensureUndoEl();
    el.querySelector('.rb-cart-undo__text').textContent = 'Removed ' + data.label;
    el.classList.add('is-visible');
    clearTimeout(undoTimer);
    undoTimer = setTimeout(hideUndo, UNDO_MS);
  }

  function hideUndo() {
    if (undoEl) undoEl.classList.remove('is-visible');
    lastRemoved = null;
  }

  function doUndo() {
    if (!lastRemoved) return hideUndo();

    var payload = {
      id: lastRemoved.id,
      quantity: lastRemoved.quantity,
      properties: lastRemoved.properties
    };
    if (lastRemoved.selling_plan) payload.selling_plan = lastRemoved.selling_plan;

    var btn = undoEl && undoEl.querySelector('.rb-cart-undo__btn');
    if (btn) btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function () { return refreshCartCache(); })
      .then(function () {
        if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
          window.ajaxCart.load();
        } else {
          window.location.reload();
        }
      })
      .catch(function () {})
      .then(function () {
        if (btn) btn.disabled = false;
        hideUndo();
      });
  }

  /* --------------------------------------------------------- enhancements */

  // Class toggle on an existing button. Adds nothing, moves nothing.
  function clampMinus(root) {
    var rows = root.querySelectorAll('.ajaxcart__row');
    [].forEach.call(rows, function (row) {
      var input = row.querySelector('.ajaxcart__qty-num');
      var minus = row.querySelector('.ajaxcart__qty--minus');
      if (!input || !minus) return;
      var qty = parseInt(String(input.value).replace(/\D/g, ''), 10) || 1;
      minus.classList.toggle('rb-qty-floor', qty <= 1);
      if (qty <= 1) minus.setAttribute('aria-disabled', 'true');
      else minus.removeAttribute('aria-disabled');
    });
  }

  /**
   * Pin the upsell rail above the footer.
   *
   * rb-cart-rail.liquid appends it inside .ajaxcart__scroller, where it scrolls
   * away. #CartContainer is already flex-column (scroller flex:1, footer
   * flex:none), so making the rail a sibling in between — with flex:none from
   * the CSS — pins it. No container styles are touched.
   */
  function pinRail(root) {
    var rail = root.querySelector('.ajaxcart__scroller .rb-cart-rail');
    if (!rail) return;
    var footer = root.querySelector('.ajaxcart__footer');
    if (!footer || footer.parentNode !== root) return; // unexpected shape: leave it alone
    root.insertBefore(rail, footer);
  }

  /**
   * "Sage / Sage / Large" → "Sage / Large".
   *
   * Combined listings add a duplicate option to the storefront product, so the
   * variant title repeats the colour. It's cosmetic, but it's the line the
   * shopper reads to confirm what they're buying, so it shouldn't look broken.
   * Only collapses ADJACENT identical segments — a genuine "Black / Black"
   * two-tone stays intact only if the values differ, which is the correct read.
   */
  function tidyVariantText(root) {
    var metas = root.querySelectorAll('.ajaxcart__product-meta');
    [].forEach.call(metas, function (el) {
      if (el.dataset.rbTidied === '1') return;
      var raw = el.textContent.trim();
      if (raw.indexOf('/') === -1) return;

      var parts = raw.split('/').map(function (p) { return p.trim(); }).filter(Boolean);
      var out = [];
      parts.forEach(function (p) {
        if (!out.length || out[out.length - 1].toLowerCase() !== p.toLowerCase()) out.push(p);
      });
      if (out.length !== parts.length) el.textContent = out.join(' / ');
      el.dataset.rbTidied = '1';
    });
  }

  /**
   * Lift the line price up next to the product name (Huckberry-style), so the
   * row reads: thumb | name … price / variant / controls — instead of leaving
   * the price stranded on the controls line.
   *
   * Skipped on discounted lines: those render an original price, a discount
   * note and a sale price together, and that group belongs in its own column.
   */
  function inlinePrice(root) {
    var rows = root.querySelectorAll('.ajaxcart__row');
    [].forEach.call(rows, function (row) {
      if (row.querySelector('.rb-price-inline')) return;           // already moved
      if (row.querySelector('.ajaxcart-item__discount')) return;   // leave discounts alone

      var prices = row.querySelectorAll('.ajaxcart-item__price');
      if (prices.length !== 1) return;                             // unit price etc. — leave alone

      var price = prices[0];
      var top = row.querySelector('.ajaxcart__product-top');
      if (!top) return;
      var cols = top.querySelectorAll('.o-layout__item');
      var textCol = cols[cols.length - 1];
      if (!textCol || textCol.contains(price)) return;

      var nameLink = textCol.querySelector('.ajaxcart__product-name');
      var anchor = nameLink ? (nameLink.closest('a') || nameLink) : null;

      price.classList.add('rb-price-inline');
      if (anchor && anchor.parentNode === textCol) {
        textCol.insertBefore(price, anchor.nextSibling);
      } else {
        textCol.insertBefore(price, textCol.firstChild);
      }
    });
  }

  /**
   * The rail's add control keeps its "+" glyph — the CSS turns it into a small
   * round button on the image corner. (An earlier pass relabelled it "Add",
   * which needed a full-width button row and made the strip heavy.) The rail's
   * own script swaps this text to "…" while adding, so leave it alone.
   */

  function enhance() {
    if (!isMobile()) return;
    var root = container();
    if (!root) return;
    clampMinus(root);
    tidyVariantText(root);
    inlinePrice(root);
    pinRail(root);
  }

  /* ---------------------------------------------------------------- swipe */

  function closeAllSwiped(except) {
    var open = document.querySelectorAll('.ajaxcart__product.rb-swiped');
    [].forEach.call(open, function (p) { if (p !== except) p.classList.remove('rb-swiped'); });
  }

  function bindSwipe() {
    var startX = null, startY = null, row = null;

    document.addEventListener('pointerdown', function (e) {
      if (!isMobile()) return;
      row = e.target.closest ? e.target.closest('.ajaxcart__product') : null;
      startX = e.clientX;
      startY = e.clientY;
    }, { passive: true });

    document.addEventListener('pointerup', function (e) {
      if (!isMobile() || !row || startX === null) { row = null; startX = null; return; }
      var dx = e.clientX - startX;
      var dy = Math.abs(e.clientY - startY);
      if (dy < SWIPE_VTOL) {
        if (dx < -SWIPE_OPEN) {
          closeAllSwiped(row);
          row.classList.add('rb-swiped');
        } else if (dx > SWIPE_SHUT) {
          row.classList.remove('rb-swiped');
        }
      }
      row = null; startX = null; startY = null;
    }, { passive: true });
  }

  /* --------------------------------------------------------------- events */

  function onDocClick(e) {
    if (!isMobile()) return;

    // Tap the revealed red panel → run the theme's own remove.
    //
    // The panel is a ::before on .ajaxcart__product and the face
    // (.ajaxcart__row) is translated off it, so a tap on the exposed strip
    // lands on the product element itself — never inside the row. Testing
    // ancestry rather than coordinates keeps this correct at any width, and
    // means the forwarded click below re-enters through the row branch instead
    // of looping back into this one.
    var swiped = e.target.closest && e.target.closest('.ajaxcart__product.rb-swiped');
    if (swiped && !(e.target.closest && e.target.closest('.ajaxcart__row'))) {
      var panelBtn = swiped.querySelector('.ajaxcart__remove');
      if (panelBtn) {
        e.preventDefault();
        e.stopPropagation();
        panelBtn.click();
        return;
      }
    }

    // Capture the line BEFORE ajaxCart mutates the cart, so Undo can rebuild it.
    var removeBtn = e.target.closest && e.target.closest('.ajaxcart__remove');
    if (removeBtn) {
      var line = parseInt(removeBtn.getAttribute('data-line'), 10);
      var data = lineDataFor(line);
      if (data) setTimeout(function () { showUndo(data); }, 500);
    }
  }

  function init() {
    refreshCartCache();
    document.addEventListener('click', onDocClick, true);
    bindSwipe();

    var root = container();
    if (root) {
      // ajaxCart re-renders the container on every cart change, and the rail
      // injects itself asynchronously — re-apply on both.
      new MutationObserver(function () {
        enhance();
        refreshCartCache();
      }).observe(root, { childList: true, subtree: true });
    }

    window.addEventListener('resize', enhance);
    enhance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
